import { h } from 'preact'
import Icon from '../assets'
import Switcher from '../control/Switcher'
import { ActionButtons, PanelHeaderWithIcon } from '../registration-rules/PanelItems'
import { InformationCallout } from '../ui-components/InformationCallout'
import InputGroup from '../ui-components/InputGroup'
import PublishedInFilterSelector from '../overview/filters/PublishedInFilterSelector'
import ButtonControl from '../button-control'

interface BodyProps {
    isCloseInactiveTopicsEnabled: boolean,
    showActionButtons: boolean,
    showValidationError: boolean,
    showCategoryDropdown: boolean,
    categories: Array<any>,
    selectedCategories?: Array<string>,
    toggleCategoryDropdown: () => void,
    closeTopicsIn?: number,
    onSave: () => void,
    onCancel: () => void,
    onInputFocus: () => void,
    onInputBlur: () => void,
    onInputChange: (value: string) => void,
    onResetCategories: () => void,
    onCloseInactiveTopicsSettingChanged: (value: boolean) => void,
    onSelectedCategoriesChanged: (id: string, data: any[]) => void,
    categoryDropdownId: string
}

export const CloseInactiveTopicsPanelBody = ({ isCloseInactiveTopicsEnabled, closeTopicsIn, categories, showActionButtons, showValidationError, onSave, onCancel, onInputFocus, selectedCategories, onCloseInactiveTopicsSettingChanged, onInputChange, toggleCategoryDropdown, showCategoryDropdown, onSelectedCategoriesChanged, onResetCategories, categoryDropdownId, onInputBlur } : BodyProps) => {
  const isInputEnabled = !isCloseInactiveTopicsEnabled
  const selectedCategoriesLength = selectedCategories?.length
  const categoriesPlaceholderText = selectedCategoriesLength === 0 ? 'All categories' : (selectedCategoriesLength === 1 ? `${selectedCategoriesLength} category selected` : `${selectedCategoriesLength} categories selected`)
  return (<div className='panel-body-container'>
    <InformationCallout
      text={`Topics by a user with the following roles are excluded from the filter: Administrator, Community Manager, Moderator, Superuser. To reopen a topic closed by this rule, simply go to the topic moderation page and reopen it again.`} />
    <div className='panel-switch-input-dropdown-container'>
      <div className='switch-icon-container'>
        <Switcher
          on={isCloseInactiveTopicsEnabled}
          customClass='panel-switch'
          labelCustomClass='panel-switch-label'
          label={<LabelWithInputGroupInText isInputEnabled={isInputEnabled}
            ageInDays={closeTopicsIn} onInputChange={onInputChange}
            onInputFocus={onInputFocus}
            onInputBlur={onInputBlur}
            hasError={showValidationError}
          />}
          onChange={() => onCloseInactiveTopicsSettingChanged(!isCloseInactiveTopicsEnabled)} />
      </div>
      <div className='panel-input-dropdown-container'>
        {showValidationError &&
          <div className='error-message-container'>
            <div><Icon customClass='icon-warning' icon='warning' /></div>
            <div className='error-message'>Please fill in the required field</div>
          </div>
        }
        <div className='dropdown-category-label'> Select which categories this rule applies to</div>
        <ButtonControl
          disabled={isInputEnabled}
          newStyle
          title={<div>
            <span className='dropdown-placeholder-text'> {categoriesPlaceholderText}</span>
            <Icon icon='chevronDown' height={16} width={16} />
          </div>}
          size='medium'
          class={`close-inactive-topics-category-open-button ${showCategoryDropdown ? 'dropdown-open' : ''} `}
          onClick={() => toggleCategoryDropdown()}
        />
        {showCategoryDropdown &&
          <div className='overview__filters-box-container'>
            <div className='overview__filters-box'>
              <PublishedInFilterSelector
                id={'category'}
                name={'Rule'}
                items={categories}
                selectedFilterItems={selectedCategories}
                disableApplyButton={false} >
                {({ isApplyButtonDisabled, data }: { isApplyButtonDisabled: boolean, data: any }) =>
                  <div>
                    <hr />
                    <div className='overview__filters-box-buttons'>
                      <ButtonControl
                        newStyle
                        size='small'
                        variant='brand'
                        title='Apply'
                        disabled={isApplyButtonDisabled}
                        onClick={() => onSelectedCategoriesChanged(categoryDropdownId, data)}
                      />
                      <ButtonControl
                        newStyle
                        size='small'
                        title='Cancel'
                        onClick={() => onResetCategories()}
                      />
                    </div>
                  </div>
                }
              </PublishedInFilterSelector>
            </div>
          </div>}
      </div>
    </div>
    {showActionButtons &&
      <ActionButtons
        onSave={() => onSave()}
        onCancel={() => onCancel()} />
    }
  </div>)
}

interface LabelWithInputProps {
    onInputChange: (value: string) => void,
    onInputFocus: () => void,
    onInputBlur: () => void,
    ageInDays?: number,
    isInputEnabled: boolean,
    hasError?: boolean
}
export const LabelWithInputGroupInText = ({ isInputEnabled, ageInDays, onInputChange, onInputFocus, hasError, onInputBlur } : LabelWithInputProps) => (
  <div className='panel-input-label'> After a topic is inactive for
    <InputGroup
      type='number'
      min={1}
      required
      disabled={isInputEnabled}
      value={ageInDays}
      hasError={hasError}
      onChange={(e: ChangeEvent) => onInputChange(e.target.value)}
      onClick={() => onInputFocus()}
      onBlur={() => onInputBlur()}
      qaClasses='qa-close-topic-age-in-days-input' /> days, close the topic for new replies.</div>
)

export const CloseInactiveTopicsPanelHeader = ({ statusText } : { statusText: string }) => (
  <PanelHeaderWithIcon
    customClass='panel-header-container'
    iconCustomClass='icon-panel-header'
    icon='noReply'
    title='Automatically close inactive topics'
    statusText={statusText} />
)
